import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertDialog, BottomCTA, Callout, Checkbox, RadioGroup, TextField, TopNavigation } from "@hamlak/ui";
import { clearLocalSession, isApiError } from "@/api/client";
import { api, call, xClient } from "@/api/spec";
import { platform } from "@/platform";
import { periodText, t } from "@/i18n/ko";
import { deleteReasonText, reasonOptions } from "@/i18n/codes";
import { useAuthStore } from "@/stores/authStore";
import { useConfigStore } from "@/stores/configStore";
import { useUiStore } from "@/stores/uiStore";
import { useMe } from "@/features/me/useMe";
import { guardStep, isDeleteStep, useDeleteFlow, type DeleteStep } from "./deleteFlow";
import catSymbolUrl from "@hamlak/ui/brand/cat-symbol.svg";
import "./delete.css";

const DETAIL_MAX = 300;
/** 상세 입력란은 "기타" 사유를 고른 때만(시안 80:2543 에는 입력란 없음, account-deletion 정책 "기타 300자 자유 입력") */
const OTHER_REASON = "other";

/**
 * F08-01~04 탈퇴 4단계(1.20). `/delete/notice → reason → confirm(사유 화면 위 다이얼로그) → done`.
 * 수치(잔액·환불 기한·재가입 제한 일수)는 GET /me·pricing 값. 안내는 "모두 확인" 체크 뒤 다음, 사유는 선택. DELETE /me {reason?, detail?} → 204 → 로컬 세션 정리 → /delete/done.
 * step 가드: 도달하지 않은 단계로 오면 notice로(새로고침 시 메모리가 비어 notice부터).
 */
export function DeletePage() {
  const { step: stepParam } = useParams<{ step: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const step: DeleteStep = isDeleteStep(stepParam) ? stepParam : "notice";
  const flow = useDeleteFlow();
  const isMember = useAuthStore((s) => s.status === "member");
  const balance = useAuthStore((s) => s.balance);
  const pricing = useConfigStore((s) => s.settings?.pricing);
  const reasons = useConfigStore((s) => s.settings?.deleteReasons ?? []);
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  useMe(isMember && step !== "done"); // 잔액을 서버 값으로 갱신

  // 가드: 순서 건너뛰기 금지. done은 세션이 끝난 뒤라 member가 아니어도 열린다.
  const back = guardStep(step, flow.reached);
  useEffect(() => {
    if (!isDeleteStep(stepParam) || back) navigate("/delete/notice", { replace: true });
  }, [stepParam, back, navigate]);
  // done 을 떠날 때 흐름을 비운다. CTA 에서 먼저 reset 하면 이 페이지가 아직 떠 있는 렌더에서 reached 가 비어 step 가드가 /delete/notice → 회원 가드(/login)로 튄다(AC0-94 에서 드러남).
  useEffect(() => {
    if (step !== "done") return;
    return () => useDeleteFlow.getState().reset();
  }, [step]);

  const [reason, setReason] = useState<string | null>(flow.reason);
  const [detail, setDetail] = useState(flow.detail);
  const [acknowledged, setAcknowledged] = useState(false);
  // confirm 다이얼로그는 라우트 단계 그 자체(취소 = /delete/reason으로 복귀).
  const confirmOpen = step === "confirm";

  const remove = useMutation({
    mutationFn: () => {
      // 사유는 선택(시안 F08-02). 상세는 "기타"일 때만 보낸다
      const r = flow.reason;
      const d = r === OTHER_REASON ? flow.detail.trim() : "";
      return call(api.DELETE("/me", { params: { header: xClient() }, body: { ...(r ? { reason: r } : {}), ...(d ? { detail: d } : {}) } }));
    },
    onSuccess: async () => {
      flow.setPublicId(useAuthStore.getState().user?.publicId ?? null); // 세션 정리 전에 회원번호를 남긴다 → done 안내
      // 서버가 토큰·쿠키를 무효화했다. 로컬 정리(analytics.setUserId(null) 포함) + 제공자 signOut + 캐시 비우기.
      await clearLocalSession();
      await platform.auth.signOut().catch(() => undefined);
      qc.clear();
      flow.advance("done");
      navigate("/delete/done", { replace: true });
    },
    onError: (e) => {
      // 네트워크 실패 → confirm 유지. 403 DELETE_FORBIDDEN(details.reason pending)은 안내(account-deletion 정책 8·10). 어드민 계정은 앱 회원과 분리돼 사유 admin·system 없음.
      if (isApiError(e) && e.code === "DELETE_FORBIDDEN") {
        const reason = (e.details as { reason?: string } | undefined)?.reason;
        showSnackbar(reason === "pending" ? t("delete.forbidden.pending") : t("delete.failed"));
        return;
      }
      showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("delete.failed"));
    },
  });

  if (back) return null;

  if (step === "done") {
    return (
      <main className="page delete__page" data-testid="delete-done">
        <div className="delete__done">
          <img src={catSymbolUrl} alt="" className="delete__doneIcon" />
          <h1 className="delete__title">{t("delete.done.title")}</h1>
          <p className="delete__doneBody">{t("delete.done.body")}</p>
          {flow.publicId && <p className="delete__doneBody">{t("delete.done.publicId", { id: flow.publicId })}</p>}
        </div>
        <BottomCTA primary={{ variant: "neutralWeak", children: t("delete.done.cta"), onClick: () => navigate("/home", { replace: true }) }} />
      </main>
    );
  }

  if (step === "reason" || step === "confirm") {
    return (
      <main className="page" data-testid={step === "confirm" ? "delete-confirm" : "delete-reason"}>
        <TopNavigation title={t("page.delete")} right={null} onBack={() => navigate("/delete/notice", { replace: true })} />
        <div className="delete__body">
          <h1 className="delete__title">{t("delete.reason.title")}</h1>
          <p className="delete__description">{t("delete.reason.description")}</p>
          <RadioGroup aria-label={t("delete.reason.title")} options={reasonOptions(reasons, deleteReasonText)} value={reason} onChange={setReason} />
          {reason === OTHER_REASON && (
            <TextField multiline rows={3} label={t("delete.reason.detail")} placeholder={t("delete.reason.detail.placeholder")} value={detail} onValueChange={setDetail} maxLength={DETAIL_MAX} />
          )}
        </div>
        <BottomCTA
          primary={{
            variant: "neutralWeak",
            children: t("delete.reason.cta"),
            onClick: () => {
              flow.setReason(reason, detail);
              flow.advance("confirm");
              navigate("/delete/confirm");
            },
          }}
        />
        <AlertDialog
          open={confirmOpen}
          tone="critical"
          title={t("delete.confirm.title")}
          description={t("delete.confirm.body", { balance: balance ?? 0 })}
          primaryLabel={t("delete.confirm.cta")}
          secondaryLabel={t("common.cancel")}
          loading={remove.isPending}
          onPrimary={() => remove.mutate()}
          onSecondary={() => navigate("/delete/reason", { replace: true })}
        />
      </main>
    );
  }

  // 고지 카드(458:3748). 환불 기한·재가입 제한은 pricing 값, 재가입 0일이면 항목 생략
  const notices: { title: string; body?: string }[] = [
    { title: t("delete.notice.data"), body: t("delete.notice.data.body") },
    { title: t("delete.notice.bricks"), body: t("delete.notice.bricks.body") },
    ...(pricing ? [{ title: t("delete.notice.refund"), body: t("delete.notice.refund.body", { period: periodText(pricing.refundWindowDays) }) }] : []),
    { title: t("delete.notice.unrecoverable") },
    ...(pricing && pricing.rejoinBlockDays > 0 ? [{ title: t("delete.rejoin", { days: pricing.rejoinBlockDays }) }] : []),
    { title: t("delete.notice.restricted"), body: t("delete.notice.restricted.body") },
  ];

  return (
    <main className="page" data-testid="delete-notice">
      <TopNavigation title={t("page.delete")} right={null} onBack={() => navigate("/settings", { replace: true })} />
      <div className="delete__body">
        <h1 className="delete__title">{t("delete.notice.title")}</h1>
        <Callout tone="warning" title={t("delete.bricks", { balance: balance ?? 0 })} description={t("delete.bricks.body")} />
        <ul className="delete__notices">
          {notices.map((n) => (
            <li key={n.title} className="delete__notice">
              <p className="delete__noticeTitle">{n.title}</p>
              {n.body && <p className="delete__noticeBody">{n.body}</p>}
            </li>
          ))}
        </ul>
        <Checkbox checked={acknowledged} onCheckedChange={setAcknowledged}>
          {t("delete.notice.ack")}
        </Checkbox>
      </div>
      <BottomCTA
        primary={{
          variant: "neutralWeak",
          children: t("common.next"),
          disabled: !acknowledged,
          onClick: () => {
            flow.advance("reason");
            navigate("/delete/reason");
          },
        }}
      />
    </main>
  );
}
